import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, timer } from 'rxjs';
import { expand, last, map, switchMap } from 'rxjs/operators';

import { ApiConfiguration } from '../api/api-configuration';
import { SupplierRequestListV1 } from '../api/models';
import { CursorPaginationRequest } from '../api/models/cursor-pagination-request';
import { SupplierStatusV1 } from '../shared/enum/SupplierStatusV1';
import { RequestsExportJobV1 } from '../api/models/requests-export-job-v-1';
import { ReportingV1Service } from '../api/services/reporting-v-1.service';
import { SupplierRequestV1Service } from '../api/services/supplier-request-v-1.service';

export interface PipelineStatusCount {
  status: SupplierStatusV1 | string;
  count: number;
}

export interface ReportingPipeline {
  statuses: PipelineStatusCount[];
  totalOpen: number;
}

export type ReportingCacheStatus = 'PENDING' | 'READY';

/** data is null while PENDING. */
export interface ReportingResult<T> {
  status: ReportingCacheStatus;
  data: T | null;
}

export interface RequestsByStatusItem {
  status: SupplierStatusV1 | string;
  count: number;
  trend: number;
}

export interface FormRequestsByStatus {
  formId: string;
  formName: string;
  statuses: RequestsByStatusItem[];
}

/** One row of the Approver statistics report (#55495). */
export interface ApproverStat {
  userId: string;
  approverName: string;
  completed: number;
  outstanding: number;
  avgResponseBusinessDays: number;
}

// Poll PENDING reporting results for up to ~60s before surfacing an error (#55669).
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

@Injectable({ providedIn: 'root' })
export class ReportingApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ApiConfiguration);
  private readonly reportingV1 = inject(ReportingV1Service);
  private readonly supplierRequestV1 = inject(SupplierRequestV1Service);

  private get base(): string {
    return `${this.config.rootUrl}/v1/reporting`;
  }

  getPipeline(): Observable<ReportingPipeline> {
    return this.http.get<ReportingPipeline>(`${this.base}/pipeline`);
  }

  /**
   * Top `size` requests across the given statuses, most-recent first. Uses the request-list endpoint
   * (not the reporting one) so supplier, form and assignedTo names come resolved, not as IDs.
   */
  getTopRequestsForStatuses(statuses: ReadonlyArray<string>, size = 5): Observable<SupplierRequestListV1[]> {
    const body: CursorPaginationRequest = {
      size,
      sortBy: 'lastModifiedByDate',
      sortDirection: 'DESC',
      filters: [{ field: 'status', operator: 'IN', value: [...statuses] }],
    };
    return this.supplierRequestV1.listSupplierRequestsV1Cursor({ body }).pipe(map((res) => res?.content ?? []));
  }

  /** from/to are inclusive ISO dates (YYYY-MM-DD). Polls the cache envelope until READY. */
  getRequestsByStatus(from: string, to: string): Observable<FormRequestsByStatus[]> {
    return this.pollUntilReady(() =>
      this.http.get<ReportingResult<FormRequestsByStatus[]>>(`${this.base}/requests/by-status`, {
        params: { from, to },
      })
    );
  }

  getApproverStats(from: string, to: string): Observable<ApproverStat[]> {
    return this.pollUntilReady(() =>
      this.http.get<ReportingResult<ApproverStat[]>>(`${this.base}/approvers`, { params: { from, to } })
    );
  }

  /** Omit formId to export every form's widget; pass it to export a single form. */
  exportRequestsByStatusCsv(from: string, to: string, formId?: string): Observable<Blob> {
    const params: Record<string, string> = { from, to };
    if (formId) params['formId'] = formId;
    return this.http.get(`${this.base}/requests/by-status/export`, { params, responseType: 'blob' });
  }

  exportApproverStatsCsv(from: string, to: string): Observable<Blob> {
    return this.http.get(`${this.base}/approvers/export`, { params: { from, to }, responseType: 'blob' });
  }

  /** All form widgets plus approver statistics in one CSV. */
  exportAllCsv(from: string, to: string): Observable<Blob> {
    return this.http.get(`${this.base}/export/all`, { params: { from, to }, responseType: 'blob' });
  }

  exportApprovedRequests(from: string, to: string): Observable<RequestsExportJobV1> {
    return this.reportingV1.initiateApprovedRequestsExportV1({ from, to });
  }

  getApprovedRequestsExportStatus(jobId: string): Observable<RequestsExportJobV1> {
    return this.reportingV1.getApprovedRequestsExportStatusV1({ jobId });
  }

  /** Re-fetches while PENDING, emits the unwrapped data once READY, errors if still PENDING after MAX_POLLS. */
  private pollUntilReady<T>(fetch: () => Observable<ReportingResult<T>>): Observable<T> {
    return fetch().pipe(
      expand((result, i) =>
        result.status === 'PENDING' && i < MAX_POLLS ? timer(POLL_INTERVAL_MS).pipe(switchMap(fetch)) : EMPTY
      ),
      last(),
      map((result) => {
        if (result.status !== 'READY') throw new Error('Reporting result timed out');
        return result.data ?? ([] as unknown as T);
      })
    );
  }
}
